if (!customElements.get('td-freeswatch-submit')) {
  class TdFreeswatchSubmit extends HTMLElement {
    constructor() {
      super();
      this.checkboxes = this.querySelectorAll('input[type="checkbox"][data-variant-id]');
    }

    connectedCallback() {
      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          this.handleCheckboxChange(e.target);
        });
      });
      this.syncCheckboxesWithCart();
      document.addEventListener('td-cart-updated', this.handleCartUpdate.bind(this));
      document.addEventListener('td-cart-refreshed', this.handleCartRefresh.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('td-cart-updated', this.handleCartUpdate.bind(this));
      document.removeEventListener('td-cart-refreshed', this.handleCartRefresh.bind(this));
    }

    handleCartRefresh(event) {
      setTimeout(() => {
        this.syncCheckboxesWithCart();
      }, 300);
    }

    handleCartUpdate(event) {
      if (event.detail && event.detail.cart) {
        this.syncCheckboxesWithCartData(event.detail.cart);
      } else {
        this.syncCheckboxesWithCart();
      }
    }

    syncCheckboxesWithCartData(cart) {
      const cartVariantIds = cart.items
        .filter(item => 
          item.properties && 
          item.properties['_Swatch Bundle'] === 'true' &&
          item.quantity > 0
        )
        .map(item => item.variant_id);

      this.checkboxes.forEach(checkbox => {
        const variantId = parseInt(checkbox.getAttribute('data-variant-id'));
        const isInCart = variantId && cartVariantIds.includes(variantId);
        const isCurrentlyChecked = checkbox.checked;
        const isLoading = checkbox.getAttribute('data-loading') === 'true';

        if (!isLoading && isCurrentlyChecked !== isInCart) {
          checkbox.checked = isInCart;
          this.updateFabricActiveState(checkbox, isInCart);
        }
      });
    }

    syncCheckboxesWithCart() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartVariantIds = cart.items
            .filter(item => 
              item.properties && 
              item.properties['_Swatch Bundle'] === 'true' &&
              item.quantity > 0
            )
            .map(item => item.variant_id);

          this.checkboxes.forEach(checkbox => {
            const variantId = parseInt(checkbox.getAttribute('data-variant-id'));
            const isInCart = variantId && cartVariantIds.includes(variantId);
            checkbox.checked = isInCart;
            this.updateFabricActiveState(checkbox, isInCart);
          });
        })
        .catch(error => {
          console.error('Error syncing checkboxes with cart:', error);
        });
    }

    updateFabricActiveState(checkbox, isActive) {
      const fabricBlock = checkbox.closest('.freeswatch-fabric-block');
      if (fabricBlock) {
        if (isActive) {
          fabricBlock.classList.add('td-fabric-active');
        } else {
          fabricBlock.classList.remove('td-fabric-active');
        }
      }
    }

    handleCheckboxChange(checkbox) {
      const variantId = parseInt(checkbox.getAttribute('data-variant-id'));
      
      if (!variantId) {
        console.error('No variant ID found for checkbox');
        return;
      }

      if (checkbox.checked) {
        this.addItemToCart(variantId, checkbox);
      } else {
        this.removeItemFromCart(variantId, checkbox);
      }
    }

    addItemToCart(variantId, checkbox) {
      this.setCheckboxLoading(checkbox, true);

      const item = {
        id: variantId,
        quantity: 1,
        properties: {
          '_Swatch Bundle': 'true'
        }
      };

      this.checkCartForDuplicate(variantId, item, checkbox);
    }

    checkCartForDuplicate(variantId, item, checkbox) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const existingVariantIds = cart.items.map(item => item.variant_id);
          
          if (existingVariantIds.includes(variantId)) {
            this.showError('This swatch bundle is already in your cart.');
            checkbox.checked = false;
            this.setCheckboxLoading(checkbox, false);
            return;
          }

          this.performAddToCart(item, checkbox);
        })
        .catch(error => {
          console.error('Error checking cart:', error);
          this.showError('Failed to access your cart. Please try again.');
          checkbox.checked = false;
          this.setCheckboxLoading(checkbox, false);
        });
    }

    performAddToCart(item, checkbox) {
      fetch('/cart/add.js?sections=th-cart-drawer,cart-icon-bubble', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: [item] })
      })
      .then(response => response.json())
      .then(data => {
        return fetch('/cart.js').then(response => response.json()).then(cart => {
          return { cart: cart, sections: data.sections };
        });
      })
      .then(result => {
        document.dispatchEvent(new CustomEvent('td-cart-updated', {
          detail: {
            cart: result.cart,
            sections: result.sections
          }
        }));
        
        this.clearError();
        this.setCheckboxLoading(checkbox, false);
      })
      .catch(error => {
        console.error('Error adding item to cart:', error);
        this.showError('Failed to add item to cart. Please try again.');
        checkbox.checked = false;
        this.setCheckboxLoading(checkbox, false);
      });
    }

    removeItemFromCart(variantId, checkbox) {
      this.setCheckboxLoading(checkbox, true);

      fetch('/cart.js')
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch cart: ${response.status}`);
          }
          return response.json();
        })
        .then(cart => {
          const cartItem = cart.items.find(item => 
            item.variant_id === variantId && 
            item.properties && 
            item.properties['_Swatch Bundle'] === 'true'
          );

          if (!cartItem) {
            throw new Error('Item not found in cart');
          }

          return this.performRemoveFromCart(cartItem, checkbox);
        })
        .catch(error => {
          console.error('Error removing item from cart:', error);
          this.showError('Failed to remove item from cart. Please try again.');
          checkbox.checked = true;
          this.setCheckboxLoading(checkbox, false);
        });
    }

    performRemoveFromCart(cartItem, checkbox) {
      const changeUrl = (window.routes || routes)?.cart_change_url || '/cart/change.js';
      const itemId = cartItem.key || cartItem.variant_id;
      
      const sections = ['th-cart-drawer', 'cart-icon-bubble'];
      
      const fetchConfig = () => ({
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });

      return fetch(changeUrl, { 
        ...fetchConfig(), 
        body: JSON.stringify({ 
          id: itemId, 
          quantity: 0,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`Cart change failed: ${response.status}`);
          });
        }
        return response.text();
      })
      .then(state => {
        const parsedState = JSON.parse(state);
        
        if (parsedState.errors) {
          throw new Error(parsedState.errors);
        }

        return fetch('/cart.js').then(response => response.json()).then(cart => {
          document.dispatchEvent(new CustomEvent('td-cart-updated', {
            detail: {
              cart: cart,
              sections: parsedState.sections || {}
            }
          }));
          this.clearError();
          this.setCheckboxLoading(checkbox, false);
        });
      });
    }

    setCheckboxLoading(checkbox, isLoading) {
      if (isLoading) {
        checkbox.disabled = true;
      } else {
        checkbox.disabled = false;
      }
    }

    showError(message) {
      const errorElement = this.querySelector('.submit-error-msg');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = 'block';
      }
    }

    clearError() {
      const errorElement = this.querySelector('.submit-error-msg');
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }

  customElements.define('td-freeswatch-submit', TdFreeswatchSubmit);
}
